// import React, { useState, useEffect } from 'react';
import { useState, useEffect } from 'react';
import { AdminGameInfo } from '../../types';
import { useNotification } from '../../contexts/NotificationContext';
import { TrashIcon } from '@heroicons/react/24/outline';

interface AdminGamesResponse {
  games: AdminGameInfo[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

export default function AdminGames() {
  const [gamesData, setGamesData] = useState<AdminGamesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const { addNotification } = useNotification();
  const [deletingGameId, setDeletingGameId] = useState<string | null>(null);

  useEffect(() => {
    fetchAdminGames(page);
  }, [page]);

  const fetchAdminGames = async (currentPage: number) => {
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/admin/games?page=${currentPage}&limit=25`, { credentials: 'include' });
      const data = await response.json();
      if (!response.ok) throw new Error((data as {error: string})?.error || 'Failed to fetch games');
      setGamesData(data as AdminGamesResponse);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load games');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteGame = async (gameId: string, gameName: string) => {
      if (!window.confirm(`Are you sure you want to DELETE game "${gameName}" and all its scores? This is IRREVERSIBLE!`)) return;
      setDeletingGameId(gameId);
      setError('');
      try {
        const response = await fetch(`/api/admin/games/${gameId}`, {
          method: 'DELETE',
          credentials: 'include',
        });
        const data = await response.json();
        if (!response.ok) throw new Error((data as {error: string})?.error || 'Failed to delete game');
        addNotification('Game deleted successfully!', 'success');
        fetchAdminGames(1); // Refresh list, go to page 1
      } catch (err) {
         const errorMsg = err instanceof Error ? err.message : 'Could not delete game';
         setError(errorMsg);
         addNotification(errorMsg, 'error');
      } finally {
         setDeletingGameId(null);
      }
  };
  
  const Pagination = () => {
      if (!gamesData || gamesData.pagination.pages <= 1) return null;
      const { page: currentPage, pages } = gamesData.pagination;
      return (
        <div className="flex justify-center items-center space-x-2 mt-6">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="btn-secondary text-sm">Prev</button>
            <span>Page {currentPage} of {pages}</span>
            <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={currentPage === pages} className="btn-secondary text-sm">Next</button>
        </div>
      );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Admin: All Games</h1>
      
      {error && ( <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">{error}</div> )}

      <div className="shadow-md dark:shadow-none rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Collection</th>
                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Settings</th>
                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created</th>
                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {isLoading ? (
                 <tr><td colSpan={5} className="text-center p-4 dark:text-gray-400">Loading...</td></tr>
              ) : !gamesData || gamesData.games.length === 0 ? (
                 <tr><td colSpan={5} className="text-center p-4 dark:text-gray-400">No games found.</td></tr>
              ) : (
                 gamesData.games.map(game => (
                  <tr key={game.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">{game.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{game.collection_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        Target: {game.target_score ?? 'N/A'}, 
                        Win: {game.winning_mode}, 
                        Pts: {game.points_type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{new Date(game.created_at).toLocaleDateString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                       <button
                         onClick={() => handleDeleteGame(game.id, game.name)}
                         disabled={deletingGameId === game.id}
                         className="p-1 rounded text-red-600 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/50 disabled:opacity-50"
                         title="Delete Game"
                       >
                          {deletingGameId === game.id ? (
                             <svg className="animate-spin h-4 w-4" /* spinner */></svg>
                          ) : (
                             <TrashIcon className="h-4 w-4" />
                          )}
                       </button>
                    </td>
                  </tr>
                 ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Pagination />
    </div>
  );
} 